import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../supabaseClient';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
});

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // 초기 세션 가져오기
    const getInitialSession = async () => {
      try {
        console.log('🔄 초기 세션 조회 시작');
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('❌ 세션 조회 오류:', error);
          // 오류 발생 시 로컬 스토리지 정리
          localStorage.removeItem('sb-clozet-auth-token');
        } else {
          console.log('✅ 세션 조회 성공:', session ? '세션 있음' : '세션 없음');
        }
        
        setSession(session);
        setUser(session?.user ?? null);
      } catch (err) {
        console.error('❌ 세션 조회 예외:', err);
        setSession(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    getInitialSession();

    // 인증 상태 변경 리스너
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('🔄 Auth 상태 변경:', event, session ? '세션 있음' : '세션 없음');
        
        if (event === 'TOKEN_REFRESHED') {
          console.log('🔄 토큰 갱신됨');
        } else if (event === 'SIGNED_OUT') {
          console.log('🚪 로그아웃됨');
        }
        
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  return (
    <AuthContext.Provider value={{ user, session, loading }}>
      {children}
    </AuthContext.Provider>
  );
}; 